import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_OG = 'https://images.unsplash.com/photo-1502933691298-84fc14542831?auto=format&fit=crop&w=1200&q=70';
const SITE = 'KITEIQ';

// Canonical production domain. All canonical/hreflang/OG URLs are normalised to
// this host so preview/staging hosts never compete for indexing (duplicate
// content) and crawlers always consolidate to the real site.
const SITE_URL = 'https://kitecoach.app';

const LANGS = ['nl', 'en', 'de'];
const OG_LOCALES = { nl: 'nl_NL', en: 'en_US', de: 'de_DE' };

// Normalise any incoming value (full URL or path) to a clean pathname.
const toPath = (input) => {
  if (!input) {
    return typeof window !== 'undefined' ? (window.location.pathname || '/') : '/';
  }
  try {
    return new URL(input, SITE_URL).pathname || '/';
  } catch {
    const p = input.split('?')[0].split('#')[0];
    return p.startsWith('/') ? p : `/${p}`;
  }
};

/**
 * SEO meta component. Relies on React 19 native hoisting of <title>/<meta>/<link>
 * to <head>. Emits canonical + multilingual hreflang alternates (NL/EN/DE +
 * x-default) for GEO targeting, syncs <html lang>, and injects optional JSON-LD
 * structured data for rich results / AI answer engines.
 */
export const Seo = ({ title, description, canonical, image, type = 'website', jsonLd, noindex = false }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const activeLang = LANGS.includes(lang) ? lang : 'nl';

  // Keep <html lang> in sync with the active language (a11y + crawlers).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = activeLang;
    }
  }, [activeLang]);

  const cleanPath = toPath(canonical);
  const canonicalUrl = `${SITE_URL}${cleanPath}`;
  // Default language (nl) + x-default use the clean URL; en/de use ?lng= variant
  // so each hreflang target resolves to the matching language client-side.
  const altHref = (l) => (l === 'nl' ? canonicalUrl : `${canonicalUrl}?lng=${l}`);

  const img = image || DEFAULT_OG;
  const fullTitle = title ? (title.includes(SITE) ? title : `${title} | ${SITE}`) : `${SITE} — AI Kitesurf Coach`;

  return (
    <>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}

      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <>
          <link rel="canonical" href={canonicalUrl} />
          {/* Multilingual hreflang alternates for GEO / language targeting */}
          {LANGS.map((l) => (
            <link key={l} rel="alternate" hrefLang={l} href={altHref(l)} />
          ))}
          <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={OG_LOCALES[activeLang]} />
      {LANGS.filter((l) => l !== activeLang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALES[l]} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {jsonLd && (
        <script
          type="application/ld+json"
          // Escape "<" so a value containing "</script>" cannot break out of the
          // tag (prevents JSON-LD based XSS). Standard safe-serialization pattern.
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
        />
      )}
    </>
  );
};

export default Seo;
